/**
 * ApplicationDetailPage
 * 
 * Page displaying detailed information about a single Forge Application.
 */
import { AppWindow, ArrowLeft, Play, Square, TriangleAlert } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Common/button';
import { Badge } from '../../components/Common/badge';
import { OneApplicationModule } from '../../components/Forge';
import forgeService from '../../services/forgeService';
import { ForgeApplicationWithContext, BuildInfo } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import notify from '@/lib/notify';

// Helper to capitalize first letter
const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper to get state badge variant based on status
const getStateBadgeVariant = (status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return 'default';
    case 'stopped':
    case 'initializing':
    case 'creating':
      return 'secondary';
    case 'error':
    case 'build_failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

// Helper to get state icon based on status
const getStateIcon = (status?: string | null) => {
  if (!status) return null;
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return <Play className="h-3 w-3" />;
    case 'stopped':
      return <Square className="h-3 w-3" />;
    case 'error':
    case 'build_failed':
      return <TriangleAlert className="h-3 w-3" />;
    default:
      return null;
  }
};

const ApplicationDetailPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);

  const [appData, setAppData] = useState<ForgeApplicationWithContext | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [triggeredBuild, setTriggeredBuild] = useState<BuildInfo | null>(null);

  const fetchAppData = async () => {
    if (!appId) return;
    try {
      // First get the application to retrieve its forge_id
      const app = await forgeService.getApplication(appId);
      
      // Then get applications with context filtered by forge_id
      const appsWithContext = await forgeService.listApplicationsWithContext(app.forge_id);
      const appWithContext = appsWithContext.find(a => a.app_id === appId);
      
      if (appWithContext) {
        setAppData(appWithContext);
      }
    } catch (err: any) {
      console.error('Error fetching app data:', err);
      // Si l'application n'existe plus (404), rediriger vers la liste
      if (err.response?.status === 404) {
        notify.error(t('forge.application.notFound', 'Application non trouvée'));
        navigate('/applications');
      }
    }
  };

  useEffect(() => {
    fetchAppData();
  }, [appId]);

  const handleTriggerBuild = async () => {
    if (!appId) return;

    setIsBuilding(true);
    try {
      const result = await forgeService.triggerApplicationBuild(appId, {
        branch: 'main',
        triggered_by: currentUser?.email || 'unknown',
      });

      notify.success(t('forge.application.buildInitialized', 'Build initialized'));

      // Build a BuildInfo from the response and pass it to OneApplicationModule
      const buildInfo: BuildInfo = {
        build_id: result.build_info?.build_id || `build-${Date.now()}`,
        status: result.build_info?.status || 'pending',
        triggered_by: currentUser?.email || 'unknown',
        branch: result.build_info?.branch || 'main',
        batch_id: result.build_info?.batch_id || null,
        commit_sha: result.build_info?.commit_sha || null,
        pipelinerun_name: result.build_info?.pipelinerun_name || null,
        component_name: result.build_info?.component_name || null,
        started_at: result.build_info?.started_at || new Date().toISOString(),
        finished_at: null,
        duration_seconds: null,
        backend_image_tag: null,
        frontend_image_tag: null,
        build_logs: '',
        error_message: null,
      };
      setTriggeredBuild(buildInfo);

      // Refetch app data to update status
      await fetchAppData();
    } catch (error: any) {
      console.error('Failed to trigger build:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.buildError', `Failed to trigger build: ${errorMessage}`));
    } finally {
      setIsBuilding(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!appId) return;

    setIsDeleting(true);
    try {
      await forgeService.deleteApplication(appId);

      notify.success(t('forge.application.deleteSuccess', 'Application deleted successfully'));
      setShowDeleteConfirm(false);

      // Navigate back to applications list after successful deletion
      navigate('/applications');
    } catch (error: any) {
      console.error('Failed to delete application:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.deleteError', `Failed to delete application: ${errorMessage}`));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnlinkApplication = async () => {
    if (!appData?.use_case_id) return;
    setIsUnlinking(true);
    try {
      await forgeService.mapUseCaseToApplication(appData.use_case_id, null);
      notify.success(t('forge.application.unlinkSuccess', 'Application unlinked successfully'));
      setShowUnlinkConfirm(false);
      await fetchAppData();
    } catch (error: any) {
      console.error('Failed to unlink application:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.unlinkError', `Failed to unlink application: ${errorMessage}`));
    } finally {
      setIsUnlinking(false);
    }
  };

  if (!appId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-destructive">{t('forge.application.invalidId', 'Invalid Application ID')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="mb-6 p-4 border border-destructive bg-destructive/10 rounded-lg">
          <div className="flex items-start gap-3">
            <TriangleAlert className="h-6 w-6 text-destructive flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">
                {t('forge.application.deleteConfirmTitle', 'Delete Application?')}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'forge.application.deleteConfirmMessage',
                  'This will permanently delete the application from Forge. This action cannot be undone.'
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteApplication}
                  disabled={isDeleting}
                  variant="destructive"
                  size="sm"
                >
                  {isDeleting
                    ? t('forge.application.deleting', 'Deleting...')
                    : t('forge.application.confirmDelete', 'Yes, Delete')}
                </Button>
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="outline"
                  size="sm"
                >
                  {t('global.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlink confirmation dialog */}
      {showUnlinkConfirm && (
        <div className="mb-6 p-4 border border-amber-400/50 bg-amber-400/10 rounded-lg">
          <div className="flex items-start gap-3">
            <TriangleAlert className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">
                {t('forge.application.unlinkConfirmTitle', 'Unlink application?')}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'forge.application.unlinkConfirmMessage',
                  'This will detach this Forge application from the use case (and its solution). The application itself will not be deleted.'
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleUnlinkApplication}
                  disabled={isUnlinking}
                  size="sm"
                >
                  {isUnlinking
                    ? t('forge.application.unlinking', 'Unlinking…')
                    : t('forge.application.confirmUnlink', 'Yes, unlink')}
                </Button>
                <Button
                  onClick={() => setShowUnlinkConfirm(false)}
                  disabled={isUnlinking}
                  variant="outline"
                  size="sm"
                >
                  {t('global.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Details */}
      <OneApplicationModule
        applicationId={appId}
        useCaseId={appData?.use_case_id}
        useCaseName={appData?.use_case_name}
        cadrageId={appData?.cadrage_id}
        cadrageName={appData?.cadrage_name}
        showHeader={true}
        showReadme={true}
        triggeredBuild={triggeredBuild}
        onBuildComplete={() => {
          setTriggeredBuild(null);
          fetchAppData();
        }}
        onBuild={handleTriggerBuild}
        onDelete={() => setShowDeleteConfirm(true)}
        onUnlinkCadrage={() => setShowUnlinkConfirm(true)}
        isBuilding={isBuilding}
        isDeleting={isDeleting}
        isUnlinking={isUnlinking}
        extraActions={
          <button
            type="button"
            onClick={() => navigate('/applications')}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-border bg-card hover:bg-accent transition-colors"
            title={t('forge.application.backToList', 'Back to Applications')}
            aria-label={t('forge.application.backToList', 'Back to Applications')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        }
      />
    </div>
  );
};

export default ApplicationDetailPage;
